// Publisher workspace — Phase-1 web Upload path (plan §7a): drag-drop HTML,
// optional title/expiry, grant access in the same flow; published-documents
// table with a manage-access dialog. The advisory self-contained validator
// result is surfaced after publishing (informational — CSP is the boundary).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'sonner';
import { UploadCloud, FileCode2, ShieldCheck, ShieldAlert, Users, X, Search, AlertTriangle, Building2, Link2 } from 'lucide-react';
import { apiFetch, ApiError } from '@/lib/api-client';
import { AppShell } from '@/components/AppShell';
import { formatDate, cn } from '@/lib/utils';

export interface PublisherOption {
  id: string;
  slug: string;
  name: string;
}

interface PubDocument {
  id: string;
  title: string;
  slug: string;
  status: 'PUBLISHED' | 'DRAFT' | 'EXPIRED' | 'DELETED';
  selfContained: boolean;
  sizeBytes: number;
  version: number;
  publisherId: string; // owner/primary
  publishers: { id: string; slug: string; name: string; isOwner: boolean; displayName: string | null }[];
  collections: { id: string; slug: string; name: string; isDefault: boolean; publisherId: string }[];
  expiresAt: string | null;
  grantCount?: number;
  createdAt: string;
  updatedAt: string;
}

interface MemberUser {
  id: string;
  email: string;
  displayName: string;
  company: string | null;
}

interface CollectionOption {
  id: string;
  slug: string;
  name: string;
  isDefault: boolean;
}

const STATUS_STYLE: Record<PubDocument['status'], string> = {
  PUBLISHED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  DRAFT: 'bg-slate-50 text-slate-600 border-slate-200',
  EXPIRED: 'bg-amber-50 text-amber-700 border-amber-200',
  DELETED: 'bg-rose-50 text-rose-700 border-rose-200',
};

function toggleInSet(set: Set<string>, id: string): Set<string> {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/** Hex SHA-256 of a file (Web Crypto; works on the HTTPS portal / localhost).
 *  Lets us detect a duplicate BEFORE uploading the bytes. */
async function sha256Hex(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function Publish() {
  const { t, i18n } = useTranslation('publish');

  // Publishers the caller may publish to — server-resolved ([P]: own admin
  // publishers; [A]: all active publishers). null = still loading.
  const [publishers, setPublishers] = useState<PublisherOption[] | null>(null);
  const [publisherId, setPublisherId] = useState<string>('');

  useEffect(() => {
    apiFetch<{ data: PublisherOption[] }>('/pub/publishers')
      .then((res) => {
        setPublishers(res.data);
        setPublisherId((prev) => prev || res.data[0]?.id || '');
      })
      .catch(() => setPublishers([]));
  }, []);

  const [docs, setDocs] = useState<PubDocument[]>([]);
  const [members, setMembers] = useState<MemberUser[]>([]);
  const [collections, setCollections] = useState<CollectionOption[]>([]);

  // Upload form
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [collectionIds, setCollectionIds] = useState<Set<string>>(new Set());
  const [grantIds, setGrantIds] = useState<Set<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Content-dedup "link instead" dialog (the same file already exists elsewhere).
  const [linkTarget, setLinkTarget] = useState<{ id: string; title: string; ownerName: string } | null>(null);
  const [linkCollectionIds, setLinkCollectionIds] = useState<Set<string>>(new Set());
  const [linkName, setLinkName] = useState('');
  const [linking, setLinking] = useState(false);

  // Access dialog
  const [accessDoc, setAccessDoc] = useState<PubDocument | null>(null);
  const [accessIds, setAccessIds] = useState<Set<string>>(new Set());
  const [accessFilter, setAccessFilter] = useState('');
  const [accessSaving, setAccessSaving] = useState(false);

  // Edit dialog
  const [editDoc, setEditDoc] = useState<PubDocument | null>(null);

  const loadDocs = useCallback(async () => {
    if (!publisherId) return;
    try {
      const res = await apiFetch<{ data: PubDocument[] }>(`/pub/documents?publisherId=${publisherId}`);
      setDocs(res.data);
    } catch {
      toast.error(t('common:error'));
    }
  }, [publisherId, t]);

  // Grantable users are a PLATFORM-WIDE directory (not scoped to the publisher):
  // any active user can be granted any document/collection.
  const loadMembers = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: MemberUser[] }>('/pub/grantees');
      setMembers(res.data);
    } catch {
      setMembers([]);
    }
  }, []);

  const loadCollections = useCallback(async () => {
    if (!publisherId) return;
    try {
      const res = await apiFetch<{ data: CollectionOption[] }>(`/pub/collections?publisherId=${publisherId}`);
      setCollections(res.data);
    } catch {
      setCollections([]);
    }
  }, [publisherId]);

  useEffect(() => {
    void loadDocs();
    void loadMembers();
    void loadCollections();
  }, [loadDocs, loadMembers, loadCollections]);

  // Grantee + collection selections belong to one publisher — never carry them
  // across a switch (stale ids would 400, or silently pre-select).
  useEffect(() => {
    setGrantIds(new Set());
    setCollectionIds(new Set());
  }, [publisherId]);

  function pickFile(f: File | null | undefined) {
    if (!f) return;
    if (!/\.html?$/i.test(f.name)) {
      toast.error(t('upload.onlyHtml'));
      return;
    }
    setFile(f);
  }

  function toggle(set: Set<string>, id: string): Set<string> {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  }

  async function handlePublish(e: React.FormEvent) {
    e.preventDefault();
    if (!file || !publisherId || uploading) return;
    setUploading(true);
    try {
      // Content-dedup precheck: if the SAME file already exists, offer to LINK it
      // to this department instead of storing a duplicate copy.
      const sha = await sha256Hex(file);
      const check = await apiFetch<{
        data: { existing: null | { id: string; title: string; ownerName: string; alreadyLinked: boolean } };
      }>(`/pub/documents/check?sha256=${sha}&publisherId=${publisherId}`);
      if (check.data.existing) {
        const ex = check.data.existing;
        if (ex.alreadyLinked) {
          toast.error(t('upload.dupSameDept'));
          return;
        }
        setLinkCollectionIds(new Set(collectionIds)); // carry the choices already made
        setLinkName(title.trim());
        setLinkTarget({ id: ex.id, title: ex.title, ownerName: ex.ownerName });
        return;
      }

      const form = new FormData();
      form.append('file', file);
      form.append('publisherId', publisherId);
      if (title.trim()) form.append('title', title.trim());
      if (collectionIds.size > 0) form.append('collectionIds', JSON.stringify([...collectionIds]));
      if (expiresAt) form.append('expiresAt', new Date(`${expiresAt}T23:59:59`).toISOString());
      if (grantIds.size > 0) form.append('grantUserIds', JSON.stringify([...grantIds]));

      const res = await apiFetch<{ data: PubDocument & { validator: { selfContained: boolean; findings: string[] } } }>(
        '/pub/documents',
        { method: 'POST', body: form },
      );

      toast.success(t('upload.success', { title: res.data.title }));
      if (res.data.validator.selfContained) {
        toast.success(t('upload.validatorOk'));
      } else {
        toast.warning(t('upload.validatorWarn', { count: res.data.validator.findings.length }), {
          description: res.data.validator.findings.join(' · '),
          duration: 9000,
        });
      }
      // Footgun guard: a document with no grantees is invisible to every viewer.
      if (grantIds.size === 0) {
        toast.warning(t('upload.noGrantsWarn'), { duration: 8000 });
      }

      setFile(null);
      setTitle('');
      setExpiresAt('');
      setCollectionIds(new Set());
      setGrantIds(new Set());
      void loadDocs();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    } finally {
      setUploading(false);
    }
  }

  async function confirmLink() {
    if (!linkTarget || !publisherId || linking) return;
    setLinking(true);
    try {
      await apiFetch(`/pub/documents/${linkTarget.id}/link`, {
        method: 'POST',
        body: JSON.stringify({
          publisherId,
          collectionIds: [...linkCollectionIds],
          ...(linkName.trim() ? { displayName: linkName.trim() } : {}),
        }),
      });
      toast.success(t('link.success'));
      setLinkTarget(null);
      setFile(null);
      setTitle('');
      setExpiresAt('');
      setCollectionIds(new Set());
      setGrantIds(new Set());
      void loadDocs();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    } finally {
      setLinking(false);
    }
  }

  async function openAccess(doc: PubDocument) {
    setAccessDoc(doc);
    setAccessFilter('');
    try {
      const res = await apiFetch<{ data: { userIds: string[] } }>(`/pub/documents/${doc.id}/access`);
      setAccessIds(new Set(res.data.userIds));
    } catch {
      setAccessIds(new Set());
    }
  }

  async function saveAccess() {
    if (!accessDoc || accessSaving) return;
    setAccessSaving(true);
    try {
      const res = await apiFetch<{ data: { added: number; removed: number } }>(
        `/pub/documents/${accessDoc.id}/access`,
        { method: 'POST', body: JSON.stringify({ userIds: [...accessIds] }) },
      );
      toast.success(t('access.saved', { added: res.data.added, removed: res.data.removed }));
      setAccessDoc(null);
      void loadDocs();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    } finally {
      setAccessSaving(false);
    }
  }

  const filteredMembers = useMemo(() => {
    const q = accessFilter.trim().toLowerCase();
    if (!q) return members;
    return members.filter(
      (m) => m.displayName.toLowerCase().includes(q) || m.email.toLowerCase().includes(q),
    );
  }, [members, accessFilter]);

  // Collections offered for ASSIGNMENT exclude the system "Non collection"
  // (an empty selection files the document there automatically).
  const userCollections = useMemo(() => collections.filter((c) => !c.isDefault), [collections]);

  if (publishers === null) {
    return (
      <AppShell>
        <div className="flex justify-center py-24">
          <div className="w-9 h-9 rounded-full border-[3px] border-slate-200 border-t-brand-rose animate-spin" />
        </div>
      </AppShell>
    );
  }

  if (publishers.length === 0) {
    return (
      <AppShell>
        <div className="text-center py-24 animate-fade-up">
          <p className="text-sm text-slate-500 max-w-md mx-auto">{t('noPublisher')}</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mb-10 animate-fade-up">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-medium tracking-tight gradient-text pb-1">{t('title')}</h1>
            <p className="text-sm font-light text-slate-500 mt-1">{t('subtitle')}</p>
          </div>
          {publishers.length > 1 && (
            <div className="flex flex-col items-end gap-1.5">
              <label className="text-xs font-semibold text-slate-500 flex items-center gap-2">
                {t('publisherLabel')}
                <select value={publisherId} onChange={(e) => setPublisherId(e.target.value)} className="paper-input w-56">
                  {publishers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
              <span className="text-[11px] font-light text-slate-400 max-w-xs text-right leading-snug">
                {t('publisherScopeHint')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Upload card ─────────────────────────────────────── */}
      <section className="mb-12 animate-fade-up [animation-delay:0.08s]">
        <div className="flex items-center gap-4 mb-4">
          <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-brand-purple shrink-0">
            {t('upload.title')}
          </h2>
          <div className="gradient-rule flex-1" />
        </div>

        <form onSubmit={(e) => void handlePublish(e)} className="rounded-2xl bg-white border border-slate-200/80 shadow-card p-6 space-y-5">
          <label
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              pickFile(e.dataTransfer.files[0]);
            }}
            className={cn(
              'flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 cursor-pointer transition-all duration-300',
              dragOver
                ? 'border-brand-rose bg-brand-rose/[0.04] scale-[1.01]'
                : 'border-slate-200 hover:border-brand-rose/50 hover:bg-slate-50/50',
            )}
          >
            <input
              type="file"
              accept=".html,.htm,text/html"
              className="sr-only"
              onChange={(e) => {
                pickFile(e.target.files?.[0]);
                // Clear the input so re-selecting the SAME file (regenerated
                // report.html) fires a change event again.
                e.target.value = '';
              }}
            />
            {file ? (
              <>
                <FileCode2 className="w-8 h-8 text-brand-rose" />
                <p className="text-sm font-semibold text-ink">{file.name}</p>
                <p className="text-xs font-light text-slate-400">{(file.size / 1024).toFixed(0)} KB</p>
              </>
            ) : (
              <>
                <div className="p-[2px] rounded-xl bg-brand-gradient">
                  <div className="p-2.5 rounded-xl bg-white">
                    <UploadCloud className="w-6 h-6 text-brand-purple" />
                  </div>
                </div>
                <p className="text-sm font-medium text-ink mt-1">{t('upload.drop')}</p>
                <p className="text-xs font-light text-brand-rose underline underline-offset-2">{t('upload.browse')}</p>
              </>
            )}
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('upload.titleLabel')}</span>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('upload.titlePlaceholder')}
                className="paper-input"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('upload.expiryLabel')}</span>
              <input
                type="date"
                value={expiresAt}
                min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="paper-input"
              />
            </label>
          </div>

          <div>
            <span className="block text-xs font-semibold text-slate-500 mb-1.5">
              {t('upload.collectionLabel')}
              <span className="font-light text-slate-400 ml-2">{t('upload.collectionHint')}</span>
            </span>
            {userCollections.length === 0 ? (
              <p className="text-xs font-light text-slate-400">{t('upload.noCollectionsYet')}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {userCollections.map((c) => {
                  const on = collectionIds.has(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setCollectionIds((s) => toggleInSet(s, c.id))}
                      className={cn(
                        'px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
                        on
                          ? 'bg-brand-purple text-white border-brand-purple shadow-sm'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-brand-purple/40 hover:text-brand-purple',
                      )}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {members.length > 0 && (
            <div>
              <span className="block text-xs font-semibold text-slate-500 mb-1.5">
                {t('upload.grantsLabel')}
                <span className="font-light text-slate-400 ml-2">{t('upload.grantsHint')}</span>
              </span>
              <div className="flex flex-wrap gap-2">
                {members.map((m) => {
                  const on = grantIds.has(m.id);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setGrantIds((s) => toggle(s, m.id))}
                      className={cn(
                        'px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
                        on
                          ? 'bg-brand-purple text-white border-brand-purple shadow-sm'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-brand-purple/40 hover:text-brand-purple',
                      )}
                    >
                      {m.displayName}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button type="submit" disabled={!file || uploading} className="btn-gradient">
              {uploading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              {uploading ? t('upload.uploading') : t('upload.submit')}
            </button>
          </div>
        </form>
      </section>

      {/* ── Documents table ─────────────────────────────────── */}
      <section className="animate-fade-up [animation-delay:0.16s]">
        <div className="flex items-center gap-4 mb-4">
          <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-brand-purple shrink-0">
            {t('docs.title')}
          </h2>
          <div className="gradient-rule flex-1" />
          <span className="text-[10px] font-semibold text-slate-400 tabular-nums">{docs.length}</span>
        </div>

        {docs.length === 0 ? (
          <p className="text-sm font-light text-slate-400 py-8 text-center">{t('docs.empty')}</p>
        ) : (
          <div className="rounded-2xl bg-white border border-slate-200/80 shadow-card overflow-x-auto custom-scrollbar">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left">
                  <th className="px-5 py-3.5 text-xs font-bold text-slate-500">{t('docs.cols.title')}</th>
                  <th className="px-3 py-3.5 text-xs font-bold text-slate-500">{t('docs.cols.status')}</th>
                  <th className="px-3 py-3.5 text-xs font-bold text-slate-500">{t('docs.cols.selfContained')}</th>
                  <th className="px-3 py-3.5 text-xs font-bold text-slate-500">{t('docs.cols.version')}</th>
                  <th className="px-3 py-3.5 text-xs font-bold text-slate-500">{t('docs.cols.updated')}</th>
                  <th className="px-3 py-3.5 text-xs font-bold text-slate-500">{t('docs.cols.access')}</th>
                  <th className="px-3 py-3.5" />
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors">
                    <td className="px-5 py-3.5 font-medium text-ink max-w-[18rem]">
                      <span className="line-clamp-1">{d.publishers.find((p) => p.id === publisherId)?.displayName || d.title}</span>
                      <span className="flex flex-wrap items-center gap-1 mt-1">
                        {d.publishers.map((p) => (
                          <span
                            key={p.id}
                            title={p.isOwner ? t('docs.ownerDept') : t('docs.linkedDept')}
                            className={cn(
                              'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold',
                              p.isOwner ? 'bg-brand-orange/10 text-brand-orange' : 'bg-slate-100 text-slate-500',
                            )}
                          >
                            <Building2 className="w-2.5 h-2.5" />
                            {p.name}
                            {p.isOwner ? ' ★' : ''}
                          </span>
                        ))}
                        {d.collections.map((c) => (
                          <span
                            key={c.id}
                            className={cn(
                              'inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold',
                              c.isDefault ? 'bg-slate-100 text-slate-400' : 'bg-brand-purple/5 text-brand-purple/70',
                            )}
                          >
                            {c.isDefault ? t('common:uncategorized') : c.name}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td className="px-3 py-3.5">
                      <span className={cn('inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border', STATUS_STYLE[d.status])}>
                        {t(`status.${d.status}`)}
                      </span>
                    </td>
                    <td className="px-3 py-3.5">
                      {d.selfContained ? (
                        <ShieldCheck className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <ShieldAlert className="w-4 h-4 text-amber-500" />
                      )}
                    </td>
                    <td className="px-3 py-3.5 text-slate-500 tabular-nums">v{d.version}</td>
                    <td className="px-3 py-3.5 text-slate-500 whitespace-nowrap">{formatDate(d.updatedAt, i18n.language)}</td>
                    <td className="px-3 py-3.5 whitespace-nowrap">
                      {(d.grantCount ?? 0) === 0 ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-amber-50 text-amber-700 border-amber-200"
                          title={t('docs.notSharedHint')}
                        >
                          <AlertTriangle className="w-3 h-3" />
                          {t('docs.notShared')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-slate-500">
                          <Users className="w-3.5 h-3.5 text-slate-400" />
                          {d.grantCount}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3.5 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setEditDoc(d)}
                        className="text-xs font-semibold text-slate-500 hover:text-brand-purple transition-colors mr-4"
                      >
                        {t('docs.edit')}
                      </button>
                      <button
                        type="button"
                        onClick={() => void openAccess(d)}
                        className="text-xs font-semibold text-brand-rose hover:text-brand-purple transition-colors"
                      >
                        {t('docs.manageAccess')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Access dialog ───────────────────────────────────── */}
      <Dialog.Root open={!!accessDoc} onOpenChange={(open) => !open && setAccessDoc(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm animate-fade-in" />
          <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-lg rounded-2xl bg-white shadow-2xl p-6 animate-dialog-in focus:outline-none max-h-[90dvh] overflow-y-auto custom-scrollbar">
            <div className="flex items-start justify-between gap-4 mb-1">
              <Dialog.Title className="text-base font-semibold text-ink">
                {t('access.title', { title: accessDoc?.title ?? '' })}
              </Dialog.Title>
              <Dialog.Close className="p-1 rounded-md text-slate-400 hover:text-ink hover:bg-slate-100 transition-colors">
                <X className="w-4 h-4" />
              </Dialog.Close>
            </div>
            <Dialog.Description className="text-xs font-light text-slate-500 mb-4">
              {t('access.subtitle')}
            </Dialog.Description>

            {members.length === 0 ? (
              <p className="text-sm font-light text-slate-400 py-6 text-center">{t('access.noUsers')}</p>
            ) : (
              <>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={accessFilter}
                    onChange={(e) => setAccessFilter(e.target.value)}
                    placeholder={t('access.searchUsers')}
                    className="paper-input !pl-9"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto custom-scrollbar -mx-2 px-2 space-y-1">
                  {filteredMembers.map((m) => {
                    const on = accessIds.has(m.id);
                    return (
                      <label
                        key={m.id}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => setAccessIds((s) => toggle(s, m.id))}
                          className="w-4 h-4 rounded accent-[#ed125f]"
                        />
                        <div className="leading-tight">
                          <p className="text-sm font-medium text-ink">{m.displayName}</p>
                          <p className="text-xs font-light text-slate-400">
                            {m.email}
                            {m.company ? ` — ${m.company}` : ''}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </>
            )}

            <div className="flex justify-end gap-3 mt-5">
              <Dialog.Close className="btn-quiet">{t('common:cancel')}</Dialog.Close>
              <button type="button" onClick={() => void saveAccess()} disabled={accessSaving} className="btn-gradient">
                {accessSaving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {t('access.save')}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* ── Link-existing (content-dedup) dialog ────────────── */}
      <Dialog.Root open={!!linkTarget} onOpenChange={(open) => !open && setLinkTarget(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm animate-fade-in" />
          <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-md rounded-2xl bg-white shadow-2xl p-6 animate-dialog-in focus:outline-none max-h-[90dvh] overflow-y-auto custom-scrollbar">
            <div className="flex items-start justify-between gap-4 mb-1">
              <Dialog.Title className="text-base font-semibold text-ink flex items-center gap-2">
                <Link2 className="w-4 h-4 text-brand-purple" />
                {t('link.title')}
              </Dialog.Title>
              <Dialog.Close className="p-1 rounded-md text-slate-400 hover:text-ink hover:bg-slate-100 transition-colors">
                <X className="w-4 h-4" />
              </Dialog.Close>
            </div>
            <Dialog.Description className="text-xs font-light text-slate-500 mb-4">
              {t('link.subtitle', { title: linkTarget?.title ?? '', owner: linkTarget?.ownerName ?? '' })}
            </Dialog.Description>

            <div className="space-y-4">
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('link.nameLabel')}</span>
                <input
                  type="text"
                  value={linkName}
                  onChange={(e) => setLinkName(e.target.value)}
                  placeholder={t('link.namePlaceholder')}
                  className="paper-input"
                />
              </label>
              <div>
                <span className="block text-xs font-semibold text-slate-500 mb-1.5">
                  {t('link.collectionsLabel')}
                  <span className="font-light text-slate-400 ml-2">{t('upload.collectionHint')}</span>
                </span>
                {userCollections.length === 0 ? (
                  <p className="text-xs font-light text-slate-400">{t('upload.noCollectionsYet')}</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {userCollections.map((c) => {
                      const on = linkCollectionIds.has(c.id);
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setLinkCollectionIds((s) => toggle(s, c.id))}
                          className={cn(
                            'px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
                            on
                              ? 'bg-brand-purple text-white border-brand-purple shadow-sm'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-brand-purple/40 hover:text-brand-purple',
                          )}
                        >
                          {c.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Dialog.Close className="btn-quiet">{t('common:cancel')}</Dialog.Close>
              <button type="button" onClick={() => void confirmLink()} disabled={linking} className="btn-gradient">
                {linking && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {t('link.submit')}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <EditDocumentDialog
        doc={editDoc}
        publisherId={publisherId}
        collections={collections}
        onClose={() => setEditDoc(null)}
        onSaved={() => {
          setEditDoc(null);
          void loadDocs();
        }}
      />
    </AppShell>
  );
}

// ── Edit document dialog (title / collection / expiry) ───────

function EditDocumentDialog({
  doc,
  publisherId,
  collections,
  onClose,
  onSaved,
}: {
  doc: PubDocument | null;
  publisherId: string;
  collections: CollectionOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation('publish');
  const [title, setTitle] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [collectionIds, setCollectionIds] = useState<Set<string>>(new Set());
  const [expiresAt, setExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);

  // Are we editing in the OWNER department's context? Owners edit the document
  // itself (title/expiry); a secondary department edits only ITS own collections,
  // an optional display-name override, and can remove the doc from itself.
  const isOwner = !!doc && doc.publisherId === publisherId;

  useEffect(() => {
    if (doc) {
      setTitle(doc.title);
      setDisplayName(doc.publishers.find((p) => p.id === publisherId)?.displayName ?? '');
      // Seed from THIS department's user collections only.
      setCollectionIds(
        new Set(doc.collections.filter((c) => !c.isDefault && c.publisherId === publisherId).map((c) => c.id)),
      );
      setExpiresAt(doc.expiresAt ? doc.expiresAt.slice(0, 10) : '');
    }
  }, [doc, publisherId]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!doc || saving) return;
    setSaving(true);
    try {
      await apiFetch(`/pub/documents/${doc.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          publisherId,
          collectionIds: [...collectionIds],
          ...(isOwner
            ? { title: title.trim(), expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : null }
            : { displayName: displayName.trim() || null }),
        }),
      });
      toast.success(t('edit.saved'));
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    } finally {
      setSaving(false);
    }
  }

  async function unlink() {
    if (!doc || saving || !window.confirm(t('edit.unlinkConfirm'))) return;
    setSaving(true);
    try {
      await apiFetch(`/pub/documents/${doc.id}/link/${publisherId}`, { method: 'DELETE' });
      toast.success(t('edit.unlinked'));
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    } finally {
      setSaving(false);
    }
  }

  const userCols = collections.filter((c) => !c.isDefault);

  return (
    <Dialog.Root open={!!doc} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm animate-fade-in" />
        <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-md rounded-2xl bg-white shadow-2xl p-6 animate-dialog-in focus:outline-none max-h-[90dvh] overflow-y-auto custom-scrollbar">
          <div className="flex items-start justify-between gap-4 mb-1">
            <Dialog.Title className="text-base font-semibold text-ink">{t('edit.title')}</Dialog.Title>
            <Dialog.Close className="p-1 rounded-md text-slate-400 hover:text-ink hover:bg-slate-100 transition-colors">
              <X className="w-4 h-4" />
            </Dialog.Close>
          </div>
          {/* For a shared doc, make clear a secondary dept only manages its own view. */}
          {!isOwner && <p className="text-xs font-light text-slate-500 mb-4">{t('edit.secondaryHint')}</p>}
          <form onSubmit={(e) => void save(e)} className={cn('space-y-4', isOwner ? 'mt-4' : '')}>
            {isOwner ? (
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('edit.titleLabel')}</span>
                <input type="text" required value={title} onChange={(e) => setTitle(e.target.value)} className="paper-input" />
              </label>
            ) : (
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('edit.displayNameLabel')}</span>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={doc?.title ?? ''}
                  className="paper-input"
                />
              </label>
            )}
            <div>
              <span className="block text-xs font-semibold text-slate-500 mb-1.5">
                {t('edit.collectionLabel')}
                <span className="font-light text-slate-400 ml-2">{t('upload.collectionHint')}</span>
              </span>
              {userCols.length === 0 ? (
                <p className="text-xs font-light text-slate-400">{t('upload.noCollectionsYet')}</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {userCols.map((c) => {
                    const on = collectionIds.has(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setCollectionIds((s) => toggleInSet(s, c.id))}
                        className={cn(
                          'px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
                          on
                            ? 'bg-brand-purple text-white border-brand-purple shadow-sm'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-brand-purple/40 hover:text-brand-purple',
                        )}
                      >
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {isOwner && (
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('edit.expiryLabel')}</span>
                <input
                  type="date"
                  value={expiresAt}
                  min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="paper-input"
                />
              </label>
            )}
            <div className="flex items-center justify-between gap-3 pt-1">
              {!isOwner ? (
                <button
                  type="button"
                  onClick={() => void unlink()}
                  disabled={saving}
                  className="text-xs font-semibold text-rose-600 hover:text-rose-700 transition-colors"
                >
                  {t('edit.unlink')}
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-3">
                <Dialog.Close type="button" className="btn-quiet">
                  {t('common:cancel')}
                </Dialog.Close>
                <button type="submit" disabled={saving || (isOwner && !title.trim())} className="btn-gradient">
                  {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                  {t('edit.save')}
                </button>
              </div>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
